import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import {
  Building2,
  Plus,
  Globe,
  Phone,
  MapPin,
  Link,
  Briefcase,
  FileText,
  Users,
  Upload,
  Mail,
  FileCheck,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Company, UpdateCompanyData } from "@/services/companyService";
import { industryAPI } from "@/services/industryService";
import { CreateIndustryModal } from "@/components/companies/CreateIndustryModal";
import { IndustryOption, CreateIndustryData } from "@/types/industry";

interface EditCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, companyData: UpdateCompanyData, rtuFile: File | null) => Promise<void>;
  company: Company | null;
  companySlug: string;
}

const SIZE_OPTIONS = [
  { value: "1-10", label: "1-10 empleados" },
  { value: "11-50", label: "11-50 empleados" },
  { value: "51-100", label: "51-100 empleados" },
  { value: "101-250", label: "101-250 empleados" },
  { value: "251-500", label: "251-500 empleados" },
  { value: "501-1000", label: "501-1000 empleados" },
  { value: "1001-5000", label: "1001-5000 empleados" },
  { value: "5001-10000", label: "Más de 5000 empleados" },
];

export function EditCompanyModal({
  isOpen,
  onClose,
  onSave,
  company,
  companySlug,
}: EditCompanyModalProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState<UpdateCompanyData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [industries, setIndustries] = useState<IndustryOption[]>([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [isIndustryModalOpen, setIsIndustryModalOpen] = useState(false);
  const [rtuFile, setRtuFile] = useState<File | null>(null);

  const loadIndustries = async () => {
    try {
      setIndustriesLoading(true);
      const industryOptions = await industryAPI.getIndustryOptions(companySlug);
      setIndustries(industryOptions);
      return industryOptions;
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar las industrias",
        variant: "destructive",
      });
      return [];
    } finally {
      setIndustriesLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && company) {
      loadIndustries().then((options) => {
        let industryId = company.industry_id ? String(company.industry_id) : "";

        const exists = options.some((opt) => opt.value === industryId);

        if ((!industryId || !exists) && company.industry) {
          const ind = company.industry as any;
          let foundOption;

          if (typeof ind === "object") {
            if (ind.id) {
              const objId = String(ind.id);
              foundOption = options.find((opt) => opt.value === objId);
            }
            if (!foundOption && ind.name) {
              const searchName = String(ind.name).trim().toLowerCase();
              foundOption = options.find(
                (opt) => opt.label.trim().toLowerCase() === searchName,
              );
            }
          } else if (typeof ind === "string") {
            const searchName = ind.trim().toLowerCase();
            foundOption = options.find(
              (opt) => opt.label.trim().toLowerCase() === searchName,
            );
          }

          if (foundOption) {
            industryId = foundOption.value;
          }
        }

        let sizeValue = company.size || "";
        // Normalizar cualquier formato anterior (con ceros o espacios) al formato simple
        if (sizeValue === "001-010" || sizeValue === "1 - 10")
          sizeValue = "1-10";
        if (sizeValue === "011-050" || sizeValue === "11 - 50")
          sizeValue = "11-50";
        if (sizeValue === "51 - 100") sizeValue = "51-100";

        setFormData({
          name: company.name || "",
          industry_id: industryId || "",
          size: sizeValue,
          phone: company.phone || "",
          website: company.website || "",
          address: company.address || "",
          logo_url: company.logo_url || "",
          notes: company.notes || "",
          parent_company_id: company.parent_company_id || null,
          slug: companySlug,
          nit: company.nit || "",
          email: company.email || "",
          rtu_url: company.rtu_url || "",
        });
      });

      setErrors({});
      setRtuFile(null);
    }
  }, [isOpen, company, companySlug]);

  const handleChange = (field: keyof UpdateCompanyData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) newErrors.name = "Este campo es obligatorio";
    if (!formData.industry_id)
      newErrors.industry_id = "Selecciona una industria";

    const phoneRegex = /^[+]?[\d\s\-()]+$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = "Formato no válido";
    }

    const urlRegex = /^(https?:\/\/)?([\w\-]+\.)+[\w\-]+(\/[\w\- .\/?%&=]*)?$/;
    if (formData.website && !urlRegex.test(formData.website)) {
      newErrors.website = "URL no válida";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast({
        title: "Error de validación",
        description: "Por favor, completa los campos obligatorios",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company || !validate()) return;

    setLoading(true);
    try {
      await onSave(company.id, formData, rtuFile);
      onClose();
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIndustry = async (data: {
    name: string;
    is_default: boolean;
  }) => {
    try {
      const industryData: CreateIndustryData = {
        name: data.name,
        is_default: data.is_default,
        is_active: true,
        slug: companySlug,
      };

      const response = await industryAPI.createIndustry(industryData);

      if (response.success) {
        await loadIndustries();

        if (response.data?.id) {
          setFormData((prev) => ({
            ...prev,
            industry_id: response.data.id,
          }));
        }

        toast({
          title: "Industria creada",
          description: `La industria "${data.name}" ha sido creada exitosamente.`,
        });

        return true;
      }
      return false;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al crear la industria",
        variant: "destructive",
      });
      return false;
    }
  };

  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX_WIDTH = 300;
          const scaleSize = MAX_WIDTH / img.width;
          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.5);
          resolve(dataUrl);
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const compressedBase64 = await compressImage(file);
        setFormData((prev) => ({ ...prev, logo_url: compressedBase64 }));
      } catch (error) {
        toast({
          title: "Error",
          description: "No se pudo procesar la imagen",
          variant: "destructive",
        });
      }
    }
  };

  if (!company) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
          <div className="bg-white border-b border-slate-100 p-6">
            <DialogHeader>
              <div className="flex items-center space-x-3">
                <div className="bg-blue-50 p-2 rounded-lg">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-semibold text-slate-900">
                    Editar Empresa
                  </DialogTitle>
                  <DialogDescription className="text-slate-600">
                    Actualiza la información de {company.name}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
          </div>

          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-slate-800">
                    Información Básica
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2 space-y-2">
                    <Label
                      htmlFor="name"
                      className="text-sm font-medium text-slate-700"
                    >
                      Nombre de la Empresa{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="name"
                        value={formData.name || ""}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="Nombre completo de la empresa"
                        className={`pl-10 ${
                          errors.name
                            ? "border-red-500 focus-visible:ring-red-500"
                            : "border-slate-200"
                        }`}
                      />
                      <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                    {errors.name && (
                      <p className="text-red-500 text-sm flex items-center">
                        <span className="mr-1">•</span> {errors.name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="industry_id"
                      className="text-sm font-medium text-slate-700"
                    >
                      Industria <span className="text-red-500">*</span>
                    </Label>
                    <div className="flex gap-2">
                      <Select
                        key={`${company.id}-${industries.length}-${industriesLoading}`}
                        value={formData.industry_id || ""}
                        onValueChange={(value) =>
                          handleChange("industry_id", value)
                        }
                      >
                        <SelectTrigger
                          className={`flex-1 ${
                            errors.industry_id
                              ? "border-red-500 focus-visible:ring-red-500"
                              : "border-slate-200"
                          }`}
                        >
                          <SelectValue placeholder="Seleccionar industria" />
                        </SelectTrigger>
                        <SelectContent>
                          {industriesLoading ? (
                            <SelectItem value="loading" disabled>
                              <div className="flex items-center justify-center py-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                <span className="ml-2">Cargando...</span>
                              </div>
                            </SelectItem>
                          ) : industries.length === 0 ? (
                            <SelectItem value="empty" disabled>
                              Sin industrias disponibles
                            </SelectItem>
                          ) : (
                            industries.map((industry) => (
                              <SelectItem
                                key={industry.value}
                                value={industry.value}
                              >
                                <div className="flex items-center justify-between">
                                  <span>{industry.label}</span>
                                  {industry.is_default && (
                                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                                      Por defecto
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setIsIndustryModalOpen(true)}
                        className="border-blue-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                        title="Crear nueva industria"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {errors.industry_id && (
                      <p className="text-red-500 text-sm flex items-center">
                        <span className="mr-1">•</span> {errors.industry_id}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="size"
                      className="text-sm font-medium text-slate-700"
                    >
                      Tamaño de la Empresa
                    </Label>
                    <Select
                      value={formData.size || ""}
                      onValueChange={(value) => handleChange("size", value)}
                    >
                      <SelectTrigger
                        className={`${
                          errors.size
                            ? "border-red-500 focus-visible:ring-red-500"
                            : "border-slate-200"
                        }`}
                        id="size"
                      >
                        <SelectValue placeholder="Seleccionar tamaño" />
                      </SelectTrigger>
                      <SelectContent>
                        {SIZE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center">
                              <Users className="h-4 w-4 mr-2 text-slate-400" />
                              {option.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.size && (
                      <p className="text-red-500 text-sm flex items-center">
                        <span className="mr-1">•</span> {errors.size}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Phone className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-slate-800">
                    Información de Contacto
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* NIT */}
                  <div className="space-y-2">
                    <Label htmlFor="nit" className="text-sm font-medium text-slate-700">
                      NIT
                    </Label>
                    <div className="relative">
                      <Input
                        id="nit"
                        value={formData.nit || ""}
                        onChange={(e) => handleChange("nit", e.target.value)}
                        placeholder="Número de Identificación Tributaria"
                        className="pl-10 border-slate-200"
                      />
                      <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                      Correo Electrónico
                    </Label>
                    <div className="relative">
                      <Input
                        id="email"
                        type="email"
                        value={formData.email || ""}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="correo@empresa.com"
                        className="pl-10 border-slate-200"
                      />
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="phone"
                      className="text-sm font-medium text-slate-700"
                    >
                      Teléfono
                    </Label>
                    <div className="relative">
                      <Input
                        id="phone"
                        value={formData.phone || ""}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="Número de teléfono"
                        className={`pl-10 ${
                          errors.phone
                            ? "border-red-500 focus-visible:ring-red-500"
                            : "border-slate-200"
                        }`}
                      />
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                    {errors.phone && (
                      <p className="text-red-500 text-sm flex items-center">
                        <span className="mr-1">•</span> {errors.phone}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="website"
                      className="text-sm font-medium text-slate-700"
                    >
                      Sitio Web
                    </Label>
                    <div className="relative">
                      <Input
                        id="website"
                        value={formData.website || ""}
                        onChange={(e) =>
                          handleChange("website", e.target.value)
                        }
                        placeholder="URL del sitio web"
                        className={`pl-10 ${
                          errors.website
                            ? "border-red-500 focus-visible:ring-red-500"
                            : "border-slate-200"
                        }`}
                      />
                      <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                    {errors.website && (
                      <p className="text-red-500 text-sm flex items-center">
                        <span className="mr-1">•</span> {errors.website}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="address"
                      className="text-sm font-medium text-slate-700"
                    >
                      Dirección
                    </Label>
                    <div className="relative">
                      <Input
                        id="address"
                        value={formData.address || ""}
                        onChange={(e) =>
                          handleChange("address", e.target.value)
                        }
                        placeholder="Dirección completa"
                        className={`pl-10 ${
                          errors.address
                            ? "border-red-500 focus-visible:ring-red-500"
                            : "border-slate-200"
                        }`}
                      />
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                    {errors.address && (
                      <p className="text-red-500 text-sm flex items-center">
                        <span className="mr-1">•</span> {errors.address}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="logo_url"
                      className="text-sm font-medium text-slate-700"
                    >
                      Logo de la Empresa
                    </Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          id="logo_url"
                          value={formData.logo_url || ""}
                          onChange={(e) =>
                            handleChange("logo_url", e.target.value)
                          }
                          placeholder="URL de la imagen o subir archivo"
                          className="pl-10 border-slate-200"
                        />
                        <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      </div>
                      <div className="relative">
                        <input
                          title="Subir logo"
                          aria-label="Subir logo"
                          type="file"
                          id="logo-upload-edit"
                          className="hidden"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="border-slate-200"
                          onClick={() =>
                            document.getElementById("logo-upload-edit")?.click()
                          }
                        >
                          <Upload className="h-4 w-4 text-slate-600" />
                        </Button>
                      </div>
                    </div>
                    {formData.logo_url && (
                      <div className="mt-2 flex items-center gap-4 p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="h-12 w-12 bg-white rounded border border-slate-200 flex items-center justify-center overflow-hidden">
                          <img
                            src={formData.logo_url}
                            alt="Vista previa"
                            className="max-h-full max-w-full object-contain"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                          />
                        </div>
                        <div className="text-xs text-slate-500">
                          <p className="font-medium text-slate-700">
                            Vista previa
                          </p>
                          <p>Así se verá el logo de la empresa</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Archivo RTU */}
                  <div className="space-y-2">
                    <Label htmlFor="rtu_file" className="text-sm font-medium text-slate-700">
                      Archivo RTU
                    </Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          id="rtu_file"
                          value={rtuFile ? rtuFile.name : formData.rtu_url ? "Documento actual en S3" : ""}
                          readOnly
                          placeholder="Sin archivo seleccionado"
                          className="pl-10 border-slate-200 bg-slate-50 text-slate-500"
                        />
                        <FileCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      </div>
                      <div className="relative">
                        <input
                          title="Subir RTU"
                          aria-label="Subir archivo RTU"
                          type="file"
                          id="rtu-upload-edit"
                          className="hidden"
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setRtuFile(file);
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="border-slate-200 shrink-0"
                          onClick={() => document.getElementById("rtu-upload-edit")?.click()}
                        >
                          <Upload className="h-4 w-4 text-slate-600" />
                        </Button>
                      </div>
                    </div>
                    {rtuFile ? (
                      <p className="text-xs text-green-600 mt-1">
                        Nuevo archivo listo para subir.
                      </p>
                    ) : formData.rtu_url ? (
                      <a href={formData.rtu_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 mt-1 hover:underline">
                        Ver documento actual
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-slate-800">
                    Información Adicional
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="parent_company_id"
                      className="text-sm font-medium text-slate-700"
                    >
                      Empresa Matriz
                    </Label>
                    <Input
                      id="parent_company_id"
                      value={formData.parent_company_id || ""}
                      onChange={(e) =>
                        handleChange("parent_company_id", e.target.value)
                      }
                      placeholder="ID de empresa matriz"
                      className="border-slate-200"
                    />
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <Label
                      htmlFor="notes"
                      className="text-sm font-medium text-slate-700"
                    >
                      Notas
                    </Label>
                    <Textarea
                      id="notes"
                      value={formData.notes || ""}
                      onChange={(e) => handleChange("notes", e.target.value)}
                      placeholder="Información adicional relevante..."
                      rows={4}
                      className="border-slate-200 resize-none"
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className="pt-6 border-t border-slate-100">
                <div className="flex items-center justify-between w-full">
                  <div className="text-sm text-slate-500">
                    <span className="text-red-500">*</span> Campos obligatorios
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      disabled={loading}
                      className="px-6 border-slate-300 hover:bg-slate-50"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        loading ||
                        !formData.name?.trim() ||
                        !formData.industry_id
                      }
                      className="px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-sm"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          Guardando...
                        </>
                      ) : (
                        "Guardar Cambios"
                      )}
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <CreateIndustryModal
        isOpen={isIndustryModalOpen}
        onClose={() => setIsIndustryModalOpen(false)}
        onSave={handleCreateIndustry}
        companySlug={companySlug}
      />
    </>
  );
}
